import React from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'
import PassImage from '../../images/guarantee.svg'
import {GuaranteeBox, MissingList, MissingListItem, StyledImage} from './styles'
import {Button} from '../../components/button'
import {Course} from '../../redux/courses'
import {useSelector} from 'react-redux'
import {selectMemberAccess} from '../../redux/members'
import Breadcrumb, {
  BreadcrumbType,
} from '../../new-dashboard/components/Breadcrumb'
import {ContentBox, NavBox, WrappedBox} from '../../styles/global'
import Sidenav from '../../new-dashboard/components/Sidenav'
import {
  selectCanClaimGuarantee,
  selectClaimEligibilityStatus,
} from '../../redux/guarantee'
import {LoadingStatuses} from '../../components/types/LoadingStatusses'
import Loading from '../../components/Loading'

import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined'

type Props = {
  courses?: Course[]
  onChangeDivision: (value: string) => void
  onChangeFailedDivision: (value: string) => void
  failedDivision: string
}

const View = ({
  courses,
  onChangeDivision,
  onChangeFailedDivision,
  failedDivision,
}: Props) => {
  const access = useSelector(selectMemberAccess)
  const guaranteeStatus = useSelector(selectClaimEligibilityStatus)
  const completions = useSelector(selectCanClaimGuarantee)

  const isLoading = guaranteeStatus === LoadingStatuses.Loading
  const isIdle = guaranteeStatus === LoadingStatuses.Idle

  const hasAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.guarantee_claim?.value) === 1
      : false

  const handleChange = (event: SelectChangeEvent) => {
    onChangeFailedDivision(event.target.value)
  }

  const getExpert = () => {
    window.open('https://www.blackspectacles.com/pricing#are5')
  }

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: 'Guarantee Claim',
      url: '#',
    },
  ]

  return (
    <WrappedBox>
      <NavBox>
        <Sidenav />
      </NavBox>
      <ContentBox>
        <Breadcrumb breadcrumbs={breadcrumbs} />
        <GuaranteeBox id="content">
          <Box textAlign={'center'}>
            <StyledImage src={PassImage} alt="Pass the ARE Guarantee" />
          </Box>
          <Typography variant="body1" textAlign={'center'} marginTop={6}>
            Pass your division or we'll pay for your retake. Our members{' '}
            <a
              href="https://learn.blackspectacles.com/pass-rate"
              target="_blank"
            >
              pass their ARE Exams at higher rates
            </a>{' '}
            than the NCARB national averages for all six divisions! We're
            confident that if you use our Expert membership to the fullest, you
            can pass too. If you don't, we're putting our money where our mouth
            is. We'll call NCARB and give you a seat credit to use toward your
            retake.
          </Typography>
          {!hasAccess && (
            <Box textAlign={'center'} marginTop={2}>
              <Button
                children={'GET EXPERT'}
                color="primary"
                onClick={getExpert}
              />
            </Box>
          )}
          <Typography variant="h2" textAlign={'center'} marginTop={3}>
            QUALIFICATIONS TO PASS THE GUARANTEE
          </Typography>
          <Typography variant="body1" marginTop={4} fontSize={'18px'}>
            <em>
              You do the work, and you get rewarded. To meet our guarantee
              standards, adhere to the following requirements
            </em>
          </Typography>
          <Typography variant="body2" marginTop={4}>
            <strong>ARE GUARANTEE REQUIREMENTS</strong>
          </Typography>
          <ul>
            <li>Be subscribed to a monthly or annual Expert plan</li>
            <li>Complete the full Black Spectacles course for the division</li>
            <li>
              Report all pass/fails in your Black Spectacles Dashboard, along
              with all scheduled exam dates
            </li>
            <li>
              Submit your NCARB AREⓇ Score Report as proof of fail within 30
              days of the exam date
            </li>
            <li>
              Once approved, request payment through NCARB within 30 days of
              your claim
            </li>
          </ul>
          <Typography variant="body1" marginTop={4} textAlign={'center'}>
            For questions or support,{' '}
            <a
              href="https://help.blackspectacles.com/en/articles/7985193-pass-the-are-guarantee"
              target="_blank"
            >
              click here to visit our support page
            </a>
            .
          </Typography>
          <Typography variant="body1" textAlign={'center'} marginTop={6}>
            *For users registered under a firm membership, speak with your
            Account Manager, or{' '}
            <a
              href="https://www.blackspectacles.com/group-guarantee"
              target="_blank"
            >
              learn more
            </a>{' '}
            about our Firm Expert Guarantee.
          </Typography>

          <FormControl fullWidth style={{marginTop: '32px'}}>
            <InputLabel id="exam-failed">Exam failed:</InputLabel>
            <Select
              labelId="exam-failed"
              id="exam-failed"
              value={failedDivision}
              label="Exam failed"
              onChange={handleChange}
            >
              <MenuItem
                value={'PCM'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'PCM',
                  )
                }
              >
                <em>PCM</em>
              </MenuItem>
              <MenuItem
                value={'PJM'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'PJM',
                  )
                }
              >
                <em>PJM</em>
              </MenuItem>
              <MenuItem
                value={'PA'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'PA',
                  )
                }
              >
                <em>PA</em>
              </MenuItem>
              <MenuItem
                value={'PPD'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'PPD',
                  )
                }
              >
                <em>PPD</em>
              </MenuItem>
              <MenuItem
                value={'PDD'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'PDD',
                  )
                }
              >
                <em>PDD</em>
              </MenuItem>
              <MenuItem
                value={'CE'}
                disabled={
                  !courses?.some(
                    item => item.abbreviation.toUpperCase() === 'CE',
                  )
                }
              >
                <em>CE</em>
              </MenuItem>
            </Select>
          </FormControl>
          {!isIdle && (
            <>
              {isLoading ? (
                <Loading />
              ) : (
                <>
                  {(completions?.hasCompleted &&
                    !completions.hasClaimedInLastThreeMonths) ||
                  completions?.hasBypassActive ? (
                    <Box textAlign={'center'} marginTop={2}>
                      <Button
                        children={'CLAIM A GUARANTEE'}
                        color="black"
                        onClick={() => onChangeDivision(failedDivision)}
                        style={{width: '70%'}}
                        disabled={failedDivision === '' || !hasAccess}
                      />
                    </Box>
                  ) : (
                    <Box marginTop={2}>
                      <Typography variant="h3" textAlign={'center'}>
                        You're not eligible to claim a guarantee.
                      </Typography>
                      <Typography
                        variant="body1"
                        textAlign={'center'}
                        marginTop={1}
                      >
                        Based on your activity, you didn’t meet the following
                        requirements:
                      </Typography>
                      <MissingList>
                        {!completions?.flashcardsCompleted && (
                          <MissingListItem>
                            <ClearOutlinedIcon fontSize="small" /> You didn’t
                            master 100% of the Flashcards
                          </MissingListItem>
                        )}
                        {!completions?.videosCompleted && (
                          <MissingListItem>
                            <ClearOutlinedIcon fontSize="small" /> You didn't
                            watch all of our Video Lectures
                          </MissingListItem>
                        )}
                        {!completions?.allQuizzesCompleted && (
                          <MissingListItem>
                            <ClearOutlinedIcon fontSize="small" /> You didn’t
                            complete 100% of the Quizzes
                          </MissingListItem>
                        )}
                        {!completions?.examTaken && (
                          <MissingListItem>
                            <ClearOutlinedIcon fontSize="small" /> You didn’t
                            take any Practice Exams
                          </MissingListItem>
                        )}
                        {completions?.hasClaimedInLastThreeMonths && (
                          <MissingListItem>
                            <ClearOutlinedIcon fontSize="small" /> You’ve
                            already claimed a guarantee within the last 3 months
                          </MissingListItem>
                        )}
                      </MissingList>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {!hasAccess && (
            <Typography variant="body1" textAlign={'center'} marginTop={6}>
              You don’t qualify to submit a claim. Next time, make sure you have
              (and use) an Expert membership and upload the record of your
              failed division attempt. Then you can ceremoniously burn it.
            </Typography>
          )}
        </GuaranteeBox>
      </ContentBox>
    </WrappedBox>
  )
}

export default View
